import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';

// Maps backend nextAction codes to (label, primary button spec).
const ACTION_META = {
  APPROVE: { label: 'Approve order', variant: 'success' },
  PLACE_ORDER: { label: 'Place order', variant: 'primary' },
  START_BEAMS: { label: 'Beam plan', variant: 'primary' },
  COMPLETE_BEAMS: { label: 'Beam plan', variant: 'primary' },
  CHOOSE_NEXT_STAGE: { label: 'Choose next stage', variant: 'primary' },
  RECEIVE_FROM_BUTTA_CUTTING: { label: 'Receive from butta cutting', variant: 'primary' },
  RECEIVE_FROM_MILL_FINISH: { label: 'Receive from mill finish', variant: 'primary' },
  RECEIVE_FROM_BORDER_CUTTING: { label: 'Receive from border cutting', variant: 'primary' },
  RECEIVE_FROM_HANDWORK: { label: 'Receive from handwork', variant: 'primary' },
  READY_FOR_DISPATCH: { label: 'Mark ready for dispatch', variant: 'primary' },
  DISPATCH: { label: 'Dispatch order', variant: 'success' },
  MARK_DELIVERED: { label: 'Mark delivered', variant: 'success' },
  RELEASE_HOLD: { label: 'Release hold', variant: 'success' },
  NONE: null,
};

export function OrderActionBar({ order, onAction }) {
  const summary = order?.beamsSummary || {};
  const nextCode = summary.nextAction || 'NONE';
  const nextLabel = summary.nextActionLabel || '';
  const onHold = order?.onHold;
  const status = order?.workflowStatus;
  const canceled = status === 'CANCELLED';
  const delivered = status === 'DELIVERED';
  const terminal = canceled || delivered;
  const meta = ACTION_META[nextCode] || null;

  // Which secondary menu items make sense right now
  const canCancel = status === 'DRAFT' || status === 'APPROVED' || status === 'PLACE_ORDER';
  const canPutInStock = status === 'READY_FOR_DISPATCH' && !onHold;

  const [menuOpen, setMenuOpen] = useState(false);
  const [nextStageOpen, setNextStageOpen] = useState(false);
  const menuRef = useRef(null);
  const nextStageRef = useRef(null);
  useEffect(() => {
    if (!menuOpen && !nextStageOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (nextStageRef.current && !nextStageRef.current.contains(e.target)) setNextStageOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen, nextStageOpen]);

  const handle = (code) => {
    setMenuOpen(false);
    setNextStageOpen(false);
    onAction(code);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {meta && !terminal ? (
        !onHold && nextCode === 'CHOOSE_NEXT_STAGE' ? (
          <div className="relative" ref={nextStageRef}>
            <Button
              variant={meta.variant}
              size="sm"
              onClick={() => setNextStageOpen((v) => !v)}
            >
              <span>{nextLabel || meta.label}</span>
              <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
            {nextStageOpen && (
              <div className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-large">
                <MenuItem onClick={() => handle('SEND_BUTTA_CUTTING')} icon="send">Send to butta cutting</MenuItem>
                <MenuItem onClick={() => handle('SEND_MILL_FINISH')} icon="send">Send to mill finish</MenuItem>
                <MenuItem onClick={() => handle('SEND_BORDER_CUTTING')} icon="send">Send to border cutting</MenuItem>
                <MenuItem onClick={() => handle('SEND_HANDWORK')} icon="send">Send to handwork</MenuItem>
                <div className="h-px bg-slate-100" />
                <MenuItem onClick={() => handle('READY_FOR_DISPATCH')} icon="check">Mark ready for dispatch</MenuItem>
              </div>
            )}
          </div>
        ) : (
          <Button
            variant={meta.variant}
            size="sm"
            onClick={() => onAction(onHold ? 'RELEASE_HOLD' : nextCode)}
          >
            {onHold ? 'Release hold' : (nextLabel || meta.label)}
          </Button>
        )
      ) : null}

      {!terminal ? (
        <div className="relative" ref={menuRef}>
          <Button variant="secondary" size="sm" onClick={() => setMenuOpen((v) => !v)}>
            <span>More</span>
            <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </Button>
          {menuOpen && (
            <div className="absolute right-0 z-20 mt-2 w-60 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-large">
              {!onHold ? (
                <MenuItem onClick={() => handle('HOLD')} icon="pause">Place on hold</MenuItem>
              ) : (
                <MenuItem onClick={() => handle('RELEASE_HOLD')} icon="play">Release hold</MenuItem>
              )}
              {canPutInStock && (
                <>
                  <div className="h-px bg-slate-100" />
                  <MenuItem onClick={() => handle('PUT_IN_STOCK')} icon="archive">Put in stock</MenuItem>
                </>
              )}
              {canCancel && (
                <>
                  <div className="h-px bg-slate-100" />
                  <MenuItem onClick={() => handle('CANCEL')} icon="x" danger>Cancel order</MenuItem>
                </>
              )}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

function MenuItem({ children, onClick, icon, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium transition-colors',
        danger ? 'text-rose-700 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50',
      ].join(' ')}
    >
      <IconGlyph name={icon} />
      <span>{children}</span>
    </button>
  );
}

function IconGlyph({ name }) {
  const c = 'h-4 w-4';
  switch (name) {
    case 'pause':
      return (
        <svg className={c} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5 4a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1zm9 0a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1z" clipRule="evenodd" />
        </svg>
      );
    case 'play':
      return (
        <svg className={c} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 6.5a.5.5 0 01.757-.429l5 3a.5.5 0 010 .858l-5 3A.5.5 0 018 12.5v-6z" clipRule="evenodd" />
        </svg>
      );
    case 'send':
      return (
        <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      );
    case 'check':
      return (
        <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'x':
      return (
        <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    case 'archive':
      return (
        <svg className={c} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      );
    default:
      return null;
  }
}
